import {HubConnection, HubConnectionState} from '@microsoft/signalr';
import { routerStore } from 'lib/components';
import { action, computed, observable, makeObservable } from 'mobx';
import {
  dismissablePromiseLoader,
  logger,
  PromiseState,
  PromiseThrottler,
  watchPromise,
  urlHelper
} from '../../../lib/helpers';
import {ClassroomHubMessage, ClassroomRole} from '../models/classroom-models';


export class ClassroomJoinStore {
  @observable.ref
  state: PromiseState<ClassroomHubMessage> | null;

  requestCount: number = 0;

  private readonly throttler: PromiseThrottler;

  constructor(readonly classroomId: number,
              readonly classroomRole: ClassroomRole,
              readonly isPreview: boolean,
            readonly isHomework: boolean) {
    makeObservable(this);
    this.state = null;
    this.throttler = new PromiseThrottler(1000, 3);
  }

  @computed
  get isPending(): boolean {
    return this.state?.isPending === true;
  }

  @computed
  get isRejected(): boolean {
    return this.state?.isRejected === true;
  }

  @computed
  get isResolved(): boolean {
    return this.state?.isResolved === true;
  }

  @computed
  get joined(): boolean {
    if (this.state === null || !this.state.isResolved)
      return false;
    let message = this.state.value;
    return (message.MessageType === 'classroom-joined');
  }

  @action
  join(connection: HubConnection) {
    if (connection.state !== HubConnectionState.Connected) {
      this.reset();
      return;
    }

    const state = this.state;

    if (state !== null && (state.isPending || state.isResolved))
      return;

    let loader: () => Promise<ClassroomHubMessage>;
    if (this.classroomRole === 'Student')
      loader = () => connection.invoke<ClassroomHubMessage>('joinAsStudent', this.classroomId);
    else if (this.classroomRole === 'Teacher')
      loader = () => connection.invoke<ClassroomHubMessage>('joinAsTeacher', this.classroomId, this.isPreview, this.isHomework);
    else
      loader = () => Promise.reject('ClassroomJoinStore: Invalid classroom role');

    this.requestCount++;
    let requestCount = this.requestCount;

    let dismissible = dismissablePromiseLoader(loader,
      () => requestCount !== this.requestCount);
    let promise = this.throttler.run(dismissible);
    promise.catch((error) => {
      logger.log('ClassroomJoinStore.error', error);
    });

    this.watchPromise(promise);
  }

  @action
  reset = () => {
    this.requestCount++;
    this.watchPromise(null);
  };

  @action
  private watchPromise = (promise: Promise<ClassroomHubMessage> | null) => {
    if (promise === null)
      this.state = null;
    else
      this.state = watchPromise(promise);
  };
}
